import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ArrowRight, Plane, Calendar, Users, MapPin, Clock, Shield } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import QuoteModal from './QuoteModal';

const ServicesSection = () => {
  const { translate } = useLanguage();
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  const services = [
    {
      title: translate('airportTransfers'),
      description: translate('airportDescription'),
      image: "https://images.unsplash.com/photo-1564181064972-432b6f96c0ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBNZXJjZWRlcyUyMGxpbW91c2luZXxlbnwwfHx8fDE3NTQ5OTQ4OTh8MA&ixlib=rb-4.1.0&q=85",
      icon: <Plane className="w-6 h-6" />,
      features: ["Genève", "Zurich", "International", "Swiss"]
    },
    {
      title: translate('limousineService'),
      description: translate('limousineDescription'),
      image: "https://images.unsplash.com/photo-1647925692939-f91260d37287?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHN1aXQlMjBkcml2ZXJ8ZW58MHx8fHwxNzU0OTk0OTEwfDA&ixlib=rb-4.1.0&q=85",
      icon: <Shield className="w-6 h-6" />,
      features: ["Mercedes S-Class", "Chauffeur VIP", translate('securityConfidentiality'), translate('prestige')]
    },
    {
      title: translate('eventsService'),
      description: translate('eventsDescription'),
      image: "https://images.unsplash.com/photo-1659611807210-917ac61ff451?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxTd2l0emVybGFuZCUyMGx1eHVyeXxlbnwwfHx8fDE3NTQ5OTQ5MTV8MA&ixlib=rb-4.1.0&q=85",
      icon: <Calendar className="w-6 h-6" />,
      features: [translate('eventsDestinations'), translate('eventsDestinations'), translate('eventsDestinations'), translate('schoolTransport')]
    }
  ];

  return (
    <section id="services" className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {translate('whatWeDo')}
          </h2>
          <h3 className="text-2xl md:text-3xl text-gray-700 mb-6">
            {translate('servicesTitle').split('\n').map((line, index) => (
              <span key={index}>
                {line}
                {index === 0 && <br />}
              </span>
            ))}
          </h3>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {translate('servicesDescription')}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {services.map((service, index) => (
            <Card key={index} className="group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 bg-white">
              <div className="aspect-video relative overflow-hidden rounded-t-lg">
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4 bg-yellow-500 text-black p-2 rounded-full">
                  {service.icon}
                </div>
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{service.description}</p>
                
                <div className="flex flex-wrap gap-2 mb-6">
                  {service.features.map((feature, idx) => (
                    <span key={idx} className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                      {feature}
                    </span>
                  ))}
                </div>
                
                <Button variant="outline" className="w-full group-hover:bg-yellow-500 group-hover:text-black group-hover:border-yellow-500 transition-colors">
                  {translate('learnMore')}
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
          <div className="text-center">
            <div className="bg-yellow-500 text-black w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8" />
            </div>
            <h4 className="text-xl font-semibold text-gray-900 mb-2">{translate('available247')}</h4>
            <p className="text-gray-600">{translate('available247Description')}</p>
          </div>
          
          <div className="text-center">
            <div className="bg-yellow-500 text-black w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8" />
            </div>
            <h4 className="text-xl font-semibold text-gray-900 mb-2">{translate('expertDrivers')}</h4>
            <p className="text-gray-600">{translate('expertDriversDescription')}</p>
          </div>
          
          <div className="text-center">
            <div className="bg-yellow-500 text-black w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <MapPin className="w-8 h-8" />
            </div>
            <h4 className="text-xl font-semibold text-gray-900 mb-2">{translate('allSwitzerland')}</h4>
            <p className="text-gray-600">{translate('allSwitzerlandDescription')}</p>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <Button 
            size="lg" 
            className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-8 py-3 text-lg rounded-full transform hover:scale-105 transition-all"
            onClick={() => setIsQuoteModalOpen(true)}
          >
            {translate('requestQuote').toUpperCase()}
          </Button>
        </div>
      </div>

      {/* Quote Modal */}
      <QuoteModal 
        isOpen={isQuoteModalOpen} 
        onClose={() => setIsQuoteModalOpen(false)} 
      />
    </section>
  );
};

export default ServicesSection;